import { useMemo } from "react";

const icons = import.meta.glob("../../../static/icons/*.svg", {
    query: "?raw",
    import: "default",
    eager: true,
});

/**
 * Icon component
 *
 * Renders an SVG icon from the static icons folder.
 *
 * @param {object} props
 * @param {string} props.name - Icon file name (without .svg)
 * @param {string} [props.size="md"] - Icon size (xs, sm, md, lg, xl)
 * @param {string} [props.wrapperClass] - Extra classes for wrapper span
 * @param {string} [props.className] - Extra classes for the SVG
 */
export default function Icon({
    name,
    size = "md",
    wrapperClass = "",
    className = "",
}) {
    const PADDING_MAP = {
        xs: "py-0 px-1",
        sm: "p-1",
        md: "p-1",
        lg: "p-2",
        xl: "p-2",
    };

    const paddingClass = PADDING_MAP[size] || "";
    const wrapperClasses = [
        // Icons inside buttons should not become the press target, or browsers may drop the button click on rerender.
        "inline-flex items-center justify-center flex-shrink-0 pointer-events-none",
        `w-scale-${size} h-scale-${size}`,
        wrapperClass,
    ]
        .filter(Boolean)
        .join(" ");

    const svgContent = useMemo(() => {
        const path = Object.keys(icons).find((p) => p.endsWith(`/${name}.svg`));

        if (!path) {
            console.warn(`Icon "${name}" not found`);
            return "";
        }

        let svg = icons[path];

        svg = svg.replace(/\s(width|height)="[^"]*"/g, "");

        const svgClasses = [`w-full h-full block pointer-events-none`, paddingClass, className]
            .filter(Boolean)
            .join(" ");

        const hasClass = svg.match(/class="([^"]*)"/);

        if (hasClass) {
            const existing = hasClass[1];
            svg = svg.replace(
                /class="([^"]*)"/,
                `class="${svgClasses} ${existing}"`
            );
        } else {
            svg = svg.replace("<svg", `<svg class="${svgClasses}"`);
        }

        return svg;
    }, [name, size, wrapperClass, className]);

    if (!svgContent) return null;

    return (
        <span
            className={wrapperClasses}
            aria-hidden="true"
            dangerouslySetInnerHTML={{ __html: svgContent }}
        />
    );
}
